import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap, type InviteSummary, type MemberEntry, type Role } from "@/api";
import { useAdminView } from "@/adminView";
import { copyText, inviteLink, usePlatformOrigin } from "@/components/ShareAirframe";
import { ErrorNote } from "@/components/ErrorNote";
import { formatDate, formatDateTime } from "@/lib/format";

export const Route = createFileRoute("/airframes/$id/members")({
  component: Members,
});

type MemberAction = "approve" | "reject" | "remove" | "ban" | "unban";

function Members() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const airframe = useQuery(queries.airframe(id));
  const me = useQuery(queries.me());
  const role = airframe.data?.your_role ?? null;
  const adminView = useAdminView();
  const manages = role === "owner" || role === "captain";
  const canList = !!role || adminView || !!airframe.data?.policy?.show_members;
  const active = useQuery({ ...queries.members(id, "active"), enabled: canList });
  const pending = useQuery({ ...queries.members(id, "pending"), enabled: manages });
  const banned = useQuery({ ...queries.members(id, "banned"), enabled: manages });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["members", id] });
    queryClient.invalidateQueries({ queryKey: ["airframe", id] });
    queryClient.invalidateQueries({ queryKey: ["airframes"] });
  };
  const act = useMutation({
    mutationFn: async ({ uid, action }: { uid: string; action: MemberAction }) =>
      unwrap(
        await api.POST("/api/v1/airframes/{id}/members/{target}", {
          params: { path: { id, target: `${uid}:${action}` } },
        }),
      ),
    onSuccess: invalidate,
  });
  const setRole = useMutation({
    mutationFn: async ({ uid, role }: { uid: string; role: Role }) =>
      unwrap(
        await api.PATCH("/api/v1/airframes/{id}/members/{target}", {
          params: { path: { id, target: uid } },
          body: { role },
        }),
      ),
    onSuccess: invalidate,
  });
  if (!airframe.data) return null;
  if (!canList) {
    return <p className="muted">The owner keeps the member list private.</p>;
  }
  const myId = me.data?.user.id;
  return (
    <div className="space-y-6">
      <ErrorNote error={act.error ?? setRole.error} />
      {manages && pending.data && pending.data.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">
            Join requests <span className="muted tnum">({pending.data.length})</span>
          </h2>
          <ul className="space-y-2" data-testid="pending-list">
            {pending.data.map((m) => (
              <li key={m.id} className="card flex flex-wrap items-center gap-3" data-testid="pending-row" data-handle={m.handle}>
                <MemberName m={m} />
                {m.message ? <span className="muted text-sm italic">"{m.message}"</span> : null}
                <span className="muted text-xs">{formatDateTime(m.since)}</span>
                <span className="ml-auto flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => act.mutate({ uid: m.id, action: "approve" })}
                    disabled={act.isPending}
                    data-testid="approve"
                  >
                    Approve
                  </button>
                  <button type="button" className="btn" onClick={() => act.mutate({ uid: m.id, action: "reject" })} disabled={act.isPending}>
                    Decline
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">
          Members <span className="muted tnum">({active.data?.length ?? 0})</span>
        </h2>
        <ErrorNote error={active.error} />
        <ul className="space-y-2" data-testid="member-list">
          {active.data?.map((m) => (
            <li key={m.id} className="card flex flex-wrap items-center gap-3" data-testid="member-row" data-handle={m.handle} data-role={m.role}>
              <MemberName m={m} />
              <span className="pill">{m.role}</span>
              <span className="muted text-xs">since {formatDate(m.since)}</span>
              {manages && m.role !== "owner" && m.id !== myId ? (
                <span className="ml-auto flex items-center gap-2">
                  {role === "owner" ? (
                    <select
                      className="input"
                      aria-label={`Role of ${m.handle}`}
                      value={m.role}
                      onChange={(e) => setRole.mutate({ uid: m.id, role: e.target.value as Role })}
                      data-testid="role-select"
                    >
                      <option value="pilot">pilot</option>
                      <option value="captain">captain</option>
                    </select>
                  ) : null}
                  <button type="button" className="btn" onClick={() => act.mutate({ uid: m.id, action: "remove" })} disabled={act.isPending}>
                    Remove
                  </button>
                  <button type="button" className="btn btn-danger" onClick={() => act.mutate({ uid: m.id, action: "ban" })} disabled={act.isPending}>
                    Ban
                  </button>
                </span>
              ) : null}
              {m.id === myId && m.role !== "owner" ? (
                <button
                  type="button"
                  className="btn ml-auto"
                  onClick={() => act.mutate({ uid: m.id, action: "remove" })}
                  disabled={act.isPending}
                  data-testid="leave"
                >
                  Leave
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
      {manages && banned.data && banned.data.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">Banned</h2>
          <ul className="space-y-2">
            {banned.data.map((m) => (
              <li key={m.id} className="card flex items-center gap-3">
                <MemberName m={m} />
                <button type="button" className="btn ml-auto" onClick={() => act.mutate({ uid: m.id, action: "unban" })} disabled={act.isPending}>
                  Unban
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {manages ? <Invites id={id} /> : null}
    </div>
  );
}

function MemberName({ m }: { m: MemberEntry }) {
  return (
    <span className="flex items-center gap-2">
      {m.avatar_url ? <img src={m.avatar_url} alt="" className="h-6 w-6 rounded-full" /> : null}
      <span className="font-medium">{m.display_name}</span>
      <span className="muted text-sm">@{m.handle}</span>
    </span>
  );
}

function Invites({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const origin = usePlatformOrigin();
  const invites = useQuery(queries.invites(id));
  const [role, setRole] = useState<Role>("pilot");
  const [lastLink, setLastLink] = useState<string | null>(null);
  // Copied to the clipboard, or false when the clipboard is out of reach (Medtner, 2026-10-31: "This needs a copy-button").
  const [copied, setCopied] = useState<boolean | null>(null);
  const create = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/v1/airframes/{id}/invites", { params: { path: { id } }, body: { role, expires_in_hours: 168 } })),
    onSuccess: (created) => {
      setLastLink(inviteLink(origin, created.code));
      setCopied(null);
      queryClient.invalidateQueries({ queryKey: ["invites", id] });
    },
  });
  const revoke = useMutation({
    mutationFn: async (iid: string) =>
      unwrap(await api.DELETE("/api/v1/airframes/{id}/invites/{iid}", { params: { path: { id, iid } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invites", id] }),
  });
  const live = (invites.data ?? []).filter((i: InviteSummary) => !i.revoked_at);
  return (
    <section className="space-y-3" data-testid="invites">
      <h2 className="text-lg font-semibold">Invite links</h2>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="label" htmlFor="invite-role">
          Role
        </label>
        <select id="invite-role" className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          <option value="pilot">pilot</option>
          <option value="captain">captain</option>
        </select>
        <button type="submit" className="btn btn-primary" disabled={create.isPending} data-testid="create-invite">
          New link (7 days)
        </button>
      </form>
      <ErrorNote error={create.error ?? revoke.error ?? invites.error} />
      {lastLink ? (
        <div className="alert alert-ok space-y-2">
          <p>Share this link once, it is not shown again.</p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="input flex-1 truncate" data-testid="invite-link">
              {lastLink}
            </code>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                void copyText(lastLink).then(setCopied);
              }}
              data-testid="copy-invite-link"
            >
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          {copied === false ? <p className="muted text-xs">The clipboard is not reachable here: select the link and copy it.</p> : null}
        </div>
      ) : null}
      {live.length > 0 ? (
        <ul className="space-y-1 text-sm">
          {live.map((i) => (
            <li key={i.id} className="flex items-center gap-3">
              <span className="pill">{i.role}</span>
              <span className="tnum">
                {i.uses}
                {i.max_uses ? ` / ${i.max_uses}` : ""} used
              </span>
              <span className="muted">expires {formatDate(i.expires_at)}</span>
              <button type="button" className="btn ml-auto" onClick={() => revoke.mutate(i.id)} disabled={revoke.isPending}>
                Revoke
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted text-sm">No live invite link.</p>
      )}
    </section>
  );
}
